import { ChakraProvider, extendTheme, ThemeConfig } from "@chakra-ui/react";
import React, { FC, ReactNode } from "react";

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const greenPallete = {
  50: "#F0FAF5",
  100: "#D5E8DE",
  200: "#A7D1BB",
  300: "#83C4A3",
  400: "#63BA8E",
  500: "#42AD77",
  600: "#2BA168",
  700: "#1E8F58",
  800: "#157A4B",
  900: "#0F663E",
};

const newGreenPallete = {
  50: "#F7FCFA",
  100: "#ECF9F2",
  200: "#D9F2E5",
  300: "#B3E5CC",
  400: "#8CD9B2",
  500: "#66CC99",
  600: "#4DB27F",
  700: "#2BA168",
  800: "#26734C",
  900: "#194D33",
};

const grayPallete = {
  50: "#F4F7FA",
  100: "#ECF0F5",
  200: "#D3DDE8",
  300: "#B2BFCE",
  400: "#93A3B5",
  500: "#75879C",
  600: "#637182",
  700: "#4E5966",
  800: "#3A424C",
  900: "#272C33",
};

const newGrayPallete = {
  50: "#F8FAFB",
  100: "#EEF2F6",
  200: "#DEE5ED",
  300: "#C2CCD6",
  400: "#A5B1C0",
  500: "#8797AB",
  600: "#6C7E93",
  700: "#576375",
  800: "#414B58",
  900: "#2B323B",
};

const yellowPallete = {
  50: "#FFFCF5",
  100: "#FEF6E6",
  200: "#FEECCD",
  300: "#FCD79C",
  400: "#FBC16A",
  500: "#F2A840",
  600: "#E98E16",
  700: "#C75605",
  800: "#953404",
  900: "#632303",
};

const redPallete = {
  50: "#FFF6F5",
  100: "#FFECEB",
  200: "#FCD2CF",
  300: "#FAA79E",
  400: "#F77B6E",
  500: "#F25240",
  600: "#E2341D",
  700: "#BD240F",
  800: "#991400",
  900: "#660E00",
};

const bluePallete = {
  50: "#F5FBFF",
  100: "#E7F4FD",
  200: "#D0EAFB",
  300: "#A1D5F7",
  400: "#71C0F4",
  500: "#42AAF0",
  600: "#1395EC",
  700: "#0F72BD",
  800: "#0B518E",
  900: "#08365E",
};

const breakpoints = {
  base: "0em", // 0px
  sm: "30em", // ~480px. em is a relative unit and is dependant on the font-size.
  md: "48em", // ~768px
  lg: "62em", // ~992px
  xl: "80em", // ~1280px
  "2xl": "96em", // ~1536px
};

/**
 * 命名は Figma 準拠
 */
export const chakraTheme = extendTheme({
  config,
  breakpoints,
  colors: {
    primary: greenPallete,
    newPrimary: newGreenPallete,
    gray: grayPallete,
    newGray: newGrayPallete,
    yellow: yellowPallete,
    red: redPallete,
    blue: bluePallete,

    darkGreen: greenPallete[800],
    green: greenPallete[600],
    mintGreen: greenPallete[200],
    lightGreen: greenPallete[50],
    logoGreen: "#008E44",

    extraGray: grayPallete[900],
    darkGray: grayPallete[600],
    shuttleGray: grayPallete[400],
    slateGray: grayPallete[200],
    whiteGray: grayPallete[50],
    backgroundGray: "#E5E5E5",

    newLightBlue: "#E7F4FD",
    newLightRed: "#FFEDEB",
    newLightYellow: "#FFF9EB",
    alertBorderBlue: "#A1D5F7",
    alertBorderRed: "#FAA79E",
    alertBorderYellow: "#FCD79C",
  },

  components: {
    Text: {
      baseStyle: {
        color: "extraGray",
        fontSize: "14px",
      },
      variants: {
        error: {
          color: "red.600",
        },
      },
    },
    FormLabel: {
      baseStyle: {
        display: "flex",
        color: "extraGray",
        fontSize: "14px",
      },
    },
    Radio: {
      defaultProps: {
        colorScheme: "primary",
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          border: "1px solid",
          borderColor: "primary.600",
          _focus: {
            boxShadow: "white",
          },
        },
      },
      sizes: {
        md: {
          control: {
            width: 5,
            height: 5,
            borderRadius: "4px",
          },
        },
      },
      defaultProps: {
        colorScheme: "primary",
      },
      // NOTE: https://github.com/chakra-ui/chakra-ui/discussions/5791#discussioncomment-2488757
      variants: {
        outline: {
          control: {
            backgroundColor: "white",
            _disabled: {
              background: "white",
              color: "gray.300",
              borderColor: "gray.300",
            },
          },
        },
        fullWidthLabel: {
          label: {
            w: "100%",
          },
        },
      },
    },
    Input: {
      defaultProps: {
        focusBorderColor: "primary.600",
      },
      variants: {
        outline: {
          field: {
            _invalid: {
              backgroundColor: "red.50",
              borderColor: "red.600",
            },
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            _invalid: {
              backgroundColor: "red.50",
              borderColor: "red.600",
            },
          },
        },
      },
    },
    Textarea: {
      defaultProps: {
        focusBorderColor: "primary.600",
      },
      variants: {
        outline: {
          _invalid: {
            backgroundColor: "red.50",
            borderColor: "red.600",
            boxShadow: "none",
          },
        },
      },
    },
    Button: {
      defaultProps: {
        colorScheme: "primary",
        defaultChecked: false,
      },
      variants: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        solid: (props: any) => {
          if (props.colorScheme === "primary") {
            return {
              backgroundColor: "primary.600", //  デフォルトだと .500 が使用されてしまうが、デザインでは 600 がベースカラー
              _hover: {
                backgroundColor: "primary.800", //　Change if needed
              },
            };
          }

          return {};
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        outline: (props: any) => {
          if (props.colorScheme === "primary") {
            return {
              _hover: {
                backgroundColor: "initial", // デフォルトだと薄いグリーンになってしまうが、白のままとする
              },
              _active: {
                backgroundColor: "initial", // 同上
              },
            };
          }

          if (props.colorScheme === "gray") {
            return {
              borderColor: "rgba(39, 44, 51, 0.3)",
              backgroundColor: "white",
              _hover: {
                backgroundColor: "initial", // デフォルトだと薄いグリーンになってしまうが、白のままとする
              },
              _active: {
                backgroundColor: "initial", // 同上
              },
            };
          }

          return {};
        },
        ghostWithoutBg: () => {
          return {
            _hover: {
              backgroundColor: "initial", // デフォルトだと薄いグリーンになってしまうが、白のままとする
            },
            _active: {
              backgroundColor: "initial", // 同上
            },
          };
        },
      },
    },
    Modal: {
      baseStyle: {
        dialogContainer: {
          "@supports(height: -webkit-fill-available)": {},
          height: "100%",
        },
      },
    },
    Switch: {
      baseStyle: {
        track: {
          bg: "gray.600",
          _checked: {
            bg: "#4DB27F",
          },
          p: 1,
        },
      },
    },
  },
});

export const MomoChakraProvider: FC<{ children: ReactNode }> = ({ children }) => {
  return <ChakraProvider theme={chakraTheme}>{children}</ChakraProvider>;
};
